import React from 'react'

import API from 'common/src/api'
import coreUtils from 'common/src/utils/coreUtils'
import {
  fetchPhoneVerificationRequired,
  runWhenPhoneVerificationRequiredReady,
} from 'business/direct_quote_request/phoneVerification'
import SocialAuthButtons from 'business/direct_quote_request/components/SocialAuthButtons'
import SocialAuthProviderPanel from 'business/direct_quote_request/components/SocialAuthProviderPanel'
import PasswordlessAuthPanel, { maskPhoneForSms } from 'business/direct_quote_request/components/PasswordlessAuthPanel'
import PhoneOtpVerification from 'business/direct_quote_request/components/steps/PhoneOtpVerification'
import PageOtpShell from 'business/direct_quote_request/components/PageOtpShell'
import StepHeader, { WizardCloseButton } from 'business/direct_quote_request/components/StepHeader'

const EMAIL_STAGE = 'email'
const SIGN_IN_STAGE = 'sign_in'
const SIGN_UP_STAGE = 'sign_up'
const OTP_STAGE = 'otp'
const CHANGE_PHONE_STAGE = 'change_phone'
const PASSWORDLESS_OPTIONS_STAGE = 'passwordless_options'
const PASSWORDLESS_OTP_STAGE = 'passwordless_otp'
const PASSWORDLESS_CREATE_PASSWORD_STAGE = 'passwordless_create_password'
const FACEBOOK_OAUTH_STAGE = 'facebook_oauth'
const GOOGLE_OAUTH_STAGE = 'google_oauth'

const TERMS_URL = 'https://www.serviceseeking.com.au/blog/terms-and-conditions/'
const PASSWORDLESS_RESEND_COOLDOWN_SECONDS = 30

const isValidEmailFormat = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const DangerAlert = ({ message }) => (
  <div className="w-full p-3 bg-base-red-50 rounded-5 flex items-center gap-2 self-stretch">
    <div className="flex-1 flex justify-start items-center gap-2">
      <hero-icon-outline name="exclamation-circle" class="h-6 w-6 text-base-red-900 flex-shrink-0" />
      <p className="text-sm font-medium text-base-red-900">{message}</p>
    </div>
  </div>
)

export default class AccountAndNotifications extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      authStage: EMAIL_STAGE,
      loading: false,
      otpPanelMounted: false,
      emailError: '',
      nameError: '',
      contactPhoneError: '',
      baseError: '',
      infoMessage: '',
      password: '',
      name: '',
      contactPhone: '',
      otpReturnStage: SIGN_UP_STAGE,
      validMobileNumber: false,
      passwordlessOtp: '',
      newPassword: '',
      confirmPassword: '',
      loadingEmailOtp: false,
      loadingSmsOtp: false,
      loadingPasswordlessSubmit: false,
      omniAuthDetails: null,
      passwordlessFromSignIn: false,
      phoneUpdateAfterOtp: false,
      openChangePhone: false,
      changePhoneError: '',
      otpSubmitError: '',
      otpInitialSetupComplete: false,
      otpInitialVerificationStatus: '',
      showNewPassword: false,
      showConfirmPassword: false,
      lastOtpMethod: null,
      passwordlessResendCooldownUntil: null,
      passwordlessResendCooldownSecondsRemaining: 0,
      oauthNewAccountPhoneCapture: false,
      phoneCaptureMode: 'contact',
    }
  }

  isPostEmailStage = (authStage) => authStage !== EMAIL_STAGE

  componentDidMount() {
    this._isMounted = true
    this.loadOAuthScripts()
    this.syncAuthStageToParent(EMAIL_STAGE)
    fetchPhoneVerificationRequired(this.props.suburbId)
    this.applyResumeFromWizard()
  }

  componentWillUnmount() {
    this._isMounted = false
    this.clearPasswordlessResendCooldownTimer()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.suburbId !== this.props.suburbId) {
      fetchPhoneVerificationRequired(this.props.suburbId)
    }

    if (
      prevProps.resumeStage !== this.props.resumeStage ||
      prevProps.resumeError !== this.props.resumeError
    ) {
      this.applyResumeFromWizard()
    }

    if (prevProps.jobSubmitError !== this.props.jobSubmitError && this.props.jobSubmitError) {
      this.applyJobSubmitError()
    }

  }

  resolveOtpReturnStage = (authStage) => {
    if (authStage === SIGN_IN_STAGE) {
      return SIGN_IN_STAGE
    }

    if (authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE) {
      return PASSWORDLESS_CREATE_PASSWORD_STAGE
    }

    return SIGN_UP_STAGE
  }

  shouldOfferPhoneOtpAfterAuth = () => {
    const { authStage, otpReturnStage, passwordlessFromSignIn } = this.state

    if (authStage === SIGN_IN_STAGE || otpReturnStage === SIGN_IN_STAGE) {
      return coreUtils.userHasValidPhone() && !coreUtils.userHasVerifiedPhone()
    }

    if (authStage === SIGN_UP_STAGE || otpReturnStage === SIGN_UP_STAGE) {
      return true
    }

    const fromPasswordlessCreatePassword = authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE ||
      otpReturnStage === PASSWORDLESS_CREATE_PASSWORD_STAGE

    return fromPasswordlessCreatePassword && !passwordlessFromSignIn
  }

  runWhenPhoneVerificationRequiredReady = (callback) => {
    runWhenPhoneVerificationRequiredReady(callback)
  }

  contactValidationUrl = () => {
    return coreUtils.phoneVerificationRequired()
      ? '/users/validate_mobile_contact_phone'
      : '/users/validate_contact_phone'
  }

  applyApiFieldErrors = (errors, fallbackMessage) => {
    const nextState = {
      nameError: '',
      contactPhoneError: '',
      baseError: '',
    }

    const errorList = this.normalizeApiErrors(errors)

    if (!errorList.length) {
      nextState.baseError = fallbackMessage
      return nextState
    }

    errorList.forEach((error) => {
      const source = error.source && error.source.pointer ? error.source.pointer : error.source
      const field = String(error.title || source || '').toLowerCase()
      const detail = error.detail || fallbackMessage

      if (field.includes('name')) {
        nextState.nameError = detail
      } else if (field.includes('phone') || field.includes('contact')) {
        nextState.contactPhoneError = detail
      } else if (!nextState.baseError) {
        nextState.baseError = detail
      }
    })

    if (!nextState.nameError && !nextState.contactPhoneError && !nextState.baseError) {
      nextState.baseError = fallbackMessage
    }

    return nextState
  }

  normalizeApiErrors = (errors) => {
    if (!errors) return []
    if (Array.isArray(errors)) return errors
    if (typeof errors === 'object') return Object.values(errors)
    return [{ detail: String(errors) }]
  }

  syncUserSessionId = (userId) => {
    if (userId && typeof $ !== 'undefined') {
      $('#user-session-id').attr('title', userId)
    }
  }

  authErrorMessage = (xhr, fallbackMessage) => {
    if (xhr && xhr.status === 401) {
      return 'We could not confirm your session. Please refresh the page and sign in again.'
    }

    return fallbackMessage
  }

  renderFormField = ({
    id,
    label,
    type = 'text',
    value,
    error,
    placeholder,
    autoComplete,
    onChange,
    hideLabel = false,
    hideLabelOnMobile = false,
    inputClassName = '',
  }) => {
    const hasError = Boolean(error)
    const labelVisibilityClass = hideLabel
      ? 'sr-only'
      : (hideLabelOnMobile ? 'hidden md:block' : '')

    return (
      <div className="space-y-1">
        <label
          className={[
            'text-sm font-medium',
            labelVisibilityClass,
            hasError ? 'text-base-red-900' : 'text-base-gray-text-main',
          ].join(' ')}
          htmlFor={id}
        >
          {label}
        </label>
        <input
          id={id}
          type={type}
          className={[
            'input-text-base w-full h-[42px] text-sm font-normal',
            inputClassName,
            hasError ? 'border-2 border-base-red-main focus:border-base-red-main' : '',
          ].join(' ')}
          placeholder={placeholder}
          value={value}
          autoComplete={autoComplete}
          onChange={onChange}
        />
        {hasError && (
          <p className="text-sm font-normal text-base-red-900">{error}</p>
        )}
      </div>
    )
  }

  trackAuthConversion = (userId, token) => {
    if (token) {
      coreUtils.appMsg({ id: userId, token: token, version: '1.1' })
    } else {
      coreUtils.appMsg({ id: userId, version: '1.1' })
    }
  }

  attachOmniAuthToUser = () => {
    const { omniAuthDetails } = this.state

    if (!omniAuthDetails || !omniAuthDetails.oauth_email_exists) {
      return $.Deferred().resolve().promise()
    }

    return $.ajax({
      url: '/users/auth/attach_oauth_uid',
      dataType: 'json',
      method: 'POST',
      data: {
        user_id: omniAuthDetails.user_id,
        oauth_uid: omniAuthDetails.oauth_uid,
        oauth_provider: omniAuthDetails.oauth_provider,
      },
    })
  }

  handleOAuthResult = (result) => {
    if (!result || !result.email) {
      this.setState({ loading: false, baseError: 'Sign-in failed. Please try again.' })
      return
    }

    this.props.onFieldChange('email', result.email)

    if (result.oauth_email_exists) {
      this.resolveExistingOAuthAccount(result)
      return
    }

    window.App = window.App || {}
    window.App.currentUserId = result.user_id
    this.setState({
      omniAuthDetails: null,
      loading: false,
      oauthNewAccountPhoneCapture: true,
      phoneCaptureMode: 'contact',
    })
    this.trackAuthConversion(result.user_id)
    this.completeAuth(result.user_id, null)
  }

  syncAuthStageToParent = (stage) => {
    if (this.props.onAuthStageChange) {
      this.props.onAuthStageChange(stage)
    }
  }

  applyResumeFromWizard = () => {
    const { resumeStage, resumeError, onResumeHandled } = this.props

    if (!resumeStage) {
      return
    }

    window.App = window.App || {}
    window.App.userPhoneIsValid = false
    window.App.userPhoneVerified = false

    if (resumeStage === CHANGE_PHONE_STAGE) {
      if (this.state.authStage === OTP_STAGE || this.state.otpPanelMounted) {
        this.setAuthStage(OTP_STAGE, {
          phoneUpdateAfterOtp: true,
          openChangePhone: true,
          changePhoneError: resumeError || '',
          otpSubmitError: '',
          loading: false,
        })
      } else {
        this.setAuthStage(CHANGE_PHONE_STAGE, {
          baseError: resumeError || '',
          phoneUpdateAfterOtp: true,
          phoneCaptureMode: 'update',
          loading: false,
        })
      }
    }

    if (onResumeHandled) {
      onResumeHandled()
    }
  }

  applyJobSubmitError = () => {
    const { jobSubmitError, onJobSubmitErrorHandled } = this.props

    if (!jobSubmitError) {
      return
    }

    if (this.state.authStage === OTP_STAGE) {
      this.setState({ otpSubmitError: jobSubmitError, loading: false })
    } else {
      this.setState({ baseError: jobSubmitError, loading: false })
    }

    if (onJobSubmitErrorHandled) {
      onJobSubmitErrorHandled()
    }
  }

  clearLoading = () => {
    this.setState((prevState) => (prevState.loading ? { loading: false } : null))
  }

  setAuthStage = (stage, extraState) => {
    const nextState = { authStage: stage, ...extraState }
    if (stage === OTP_STAGE) {
      nextState.otpPanelMounted = true
    }
    this.setState(nextState, () => {
      this.syncAuthStageToParent(stage)
    })
  }

  loadOAuthScripts = () => {
    if (typeof window.fbAsyncInit !== 'function') {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: '482010146568223',
          cookie: true,
          version: 'v2.2',
          autoLogAppEvents: true,
          xfbml: true,
          status: true,
        })
      }
    }

    if (!document.getElementById('facebook-jssdk')) {
      const js = document.createElement('script')
      js.id = 'facebook-jssdk'
      js.src = '//connect.facebook.net/en_US/sdk.js'
      const fjs = document.getElementsByTagName('script')[0]
      fjs.parentNode.insertBefore(js, fjs)
    }

    if (!window.gpAsyncInit) {
      window.gpAsyncInit = function () {}
      $.ajax({
        url: 'https://apis.google.com/js/client:plus.js?onload=gpAsyncInit',
        dataType: 'script',
        cache: true,
      })
    }
  }

  trimmedEmail = () => (this.props.email || '').replace(/\s/g, '')

  handleEmailChange = (e) => {
    const email = e.target.value.replace(/\s/g, '')
    this.props.onFieldChange('email', email)
    this.setState({
      emailError: '',
      baseError: '',
      infoMessage: '',
      omniAuthDetails: null,
    })
  }

  handleEmailContinue = () => {
    const email = this.trimmedEmail()

    if (!email) {
      this.setState({ emailError: 'Please enter an email address' })
      return
    }

    if (!isValidEmailFormat(email)) {
      this.setState({ emailError: 'Please enter a valid email address.' })
      return
    }

    this.setState({ loading: true, emailError: '', baseError: '', infoMessage: '' })

    $.ajax(API.V3.userCheckEmail(email))
      .fail(() => {
        document.location.href = '/500'
      })
      .done((response) => {
        const result = response.meta

        if (result.status === 'true') {
          this.setState({ contactPhone: result.phone || '' }, () => {
            this.applyPasswordlessCheckResult(result)
          })
          return
        }

        if (result.status === 'valid') {
          this.setAuthStage(SIGN_UP_STAGE, {
            loading: false,
            otpReturnStage: SIGN_UP_STAGE,
          })
          return
        }

        const apiError = result.error
        const emailError = Array.isArray(apiError)
          ? apiError.join('. ')
          : (apiError || 'Please enter a valid email address.')

        this.setState({
          loading: false,
          emailError: emailError,
        })
      })
  }

  applyPasswordlessCheckResult = (data) => {
    const validMobileNumber = Boolean(data.validMobileNumber)

    if (data.passwordless) {
      this.setAuthStage(PASSWORDLESS_OPTIONS_STAGE, {
        loading: false,
        validMobileNumber,
        passwordlessFromSignIn: false,
        baseError: '',
        infoMessage: '',
        lastOtpMethod: null,
      })
      return
    }

    this.setAuthStage(SIGN_IN_STAGE, {
      loading: false,
      validMobileNumber,
      passwordlessFromSignIn: false,
      otpReturnStage: SIGN_IN_STAGE,
      lastOtpMethod: null,
    })
  }

  resolveExistingOAuthAccount = (omniAuthDetails) => {
    const email = (omniAuthDetails.email || this.trimmedEmail()).replace(/\s/g, '')

    this.setState({
      loading: true,
      omniAuthDetails,
      baseError: '',
      infoMessage: '',
    })

    $.ajax(API.V3.userCheckEmail(email))
      .fail(() => {
        document.location.href = '/500'
      })
      .done((checkEmailResponse) => {
        const checkEmailResult = checkEmailResponse.meta

        if (checkEmailResult.status !== 'true') {
          const apiError = checkEmailResult.error
          const message = Array.isArray(apiError)
            ? apiError.join('. ')
            : (apiError || 'There was a problem accessing your account. Please try again.')

          this.setState({
            loading: false,
            baseError: message,
            omniAuthDetails: null,
            infoMessage: '',
          })
          return
        }

        this.setState({ contactPhone: checkEmailResult.phone || '' }, () => {
          this.applyPasswordlessCheckResult(checkEmailResult)
        })
      })
  }

  sendPasswordlessOtp = (method, { isResend = false } = {}) => {
    if (isResend && !this.canResendPasswordlessOtp()) return

    const email = this.trimmedEmail()
    const loadingKey = method === 'sms' ? 'loadingSmsOtp' : 'loadingEmailOtp'
    const maskedPhone = maskPhoneForSms(this.state.contactPhone)

    this.setState({
      [loadingKey]: true,
      baseError: '',
      ...(isResend ? { infoMessage: '' } : {}),
    })

    $.ajax(API.V3.userPinSend({ email: email, channel: method === 'sms' ? 'sms' : 'email' }))
      .fail(() => {
        document.location.href = '/500'
      })
      .done(() => {
        const infoMessage = isResend
          ? this.passwordlessOtpResentMessage(method, maskedPhone)
          : this.passwordlessOtpSentMessage(method)

        if (isResend) {
          this.startPasswordlessResendCooldown()
        }

        this.setAuthStage(PASSWORDLESS_OTP_STAGE, {
          loadingEmailOtp: false,
          loadingSmsOtp: false,
          passwordlessOtp: '',
          lastOtpMethod: method,
          infoMessage,
        })
      })
      .always(() => {
        this.setState({ loadingEmailOtp: false, loadingSmsOtp: false })
      })
  }

  passwordlessOtpSentMessage = (method) => {
    if (method === 'sms') {
      return 'Please check your SMS—we’ve sent you instructions to create a password.'
    }

    return 'Please check your email—we’ve sent you the code to create a password.'
  }

  passwordlessOtpResentMessage = (method, maskedPhone) => {
    if (method === 'sms') {
      if (maskedPhone) {
        return `We've sent a new code to ${maskedPhone}.`
      }

      return "We've sent a new code to your mobile."
    }

    return "We've sent a new code to your email."
  }

  clearPasswordlessResendCooldownTimer = () => {
    if (this.passwordlessResendCooldownTimer) {
      clearInterval(this.passwordlessResendCooldownTimer)
      this.passwordlessResendCooldownTimer = null
    }
  }

  isPasswordlessResendOnCooldown = () => {
    const { passwordlessResendCooldownUntil } = this.state
    return Boolean(passwordlessResendCooldownUntil && Date.now() < passwordlessResendCooldownUntil)
  }

  canResendPasswordlessOtp = () => {
    const { loadingEmailOtp, loadingSmsOtp, loading } = this.state
    return !loading && !loadingEmailOtp && !loadingSmsOtp && !this.isPasswordlessResendOnCooldown()
  }

  startPasswordlessResendCooldown = () => {
    const passwordlessResendCooldownUntil = Date.now() + (PASSWORDLESS_RESEND_COOLDOWN_SECONDS * 1000)

    this.setState({
      passwordlessResendCooldownUntil,
      passwordlessResendCooldownSecondsRemaining: PASSWORDLESS_RESEND_COOLDOWN_SECONDS,
    })

    this.clearPasswordlessResendCooldownTimer()
    this.passwordlessResendCooldownTimer = setInterval(() => {
      if (!this._isMounted) {
        this.clearPasswordlessResendCooldownTimer()
        return
      }

      const remaining = Math.max(
        0,
        Math.ceil((passwordlessResendCooldownUntil - Date.now()) / 1000)
      )

      if (remaining === 0) {
        this.clearPasswordlessResendCooldownTimer()
        this.setState({ passwordlessResendCooldownUntil: null, passwordlessResendCooldownSecondsRemaining: 0 })
        return
      }

      this.setState({ passwordlessResendCooldownSecondsRemaining: remaining })
    }, 1000)
  }

  clearPasswordlessResendFeedback = () => {
    this.clearPasswordlessResendCooldownTimer()
    this.setState({
      passwordlessResendCooldownUntil: null,
      passwordlessResendCooldownSecondsRemaining: 0,
    })
  }

  handlePasswordlessOtpSubmit = () => {
    const email = this.trimmedEmail()
    const { passwordlessOtp } = this.state

    if (!passwordlessOtp) {
      this.setState({ baseError: 'Please enter the code we sent you' })
      return
    }

    this.setState({ loadingPasswordlessSubmit: true, baseError: '' })

    $.ajax(API.V3.userPinValidate({ email: email, pin: passwordlessOtp }))
      .done((data) => {
        if (data.meta.valid === true) {
          this.setAuthStage(PASSWORDLESS_CREATE_PASSWORD_STAGE, {
            loadingPasswordlessSubmit: false,
            infoMessage: '',
            newPassword: '',
            confirmPassword: '',
            showNewPassword: false,
            showConfirmPassword: false,
          })
          return
        }

        this.setState({
          loadingPasswordlessSubmit: false,
          baseError: 'Wrong code. Try again.',
        })
      })
      .fail(() => {
        document.location.href = '/500'
      })
      .always(() => {
        this.setState({ loadingPasswordlessSubmit: false })
      })
  }

  handlePasswordlessCreatePassword = () => {
    const email = this.trimmedEmail()
    const { passwordlessOtp, newPassword, confirmPassword } = this.state

    if (!newPassword || newPassword.length < 6) {
      this.setState({ baseError: 'Password is too short (min 6 characters).' })
      return
    }

    if (newPassword.length > 40) {
      this.setState({ baseError: 'Password is too long (max 40 characters).' })
      return
    }

    if (newPassword !== confirmPassword) {
      this.setState({ baseError: 'Your password and confirmation do not match. Try again.' })
      return
    }

    this.setState({ loading: true, baseError: '' })

    $.ajax(API.V3.userPasswordReset({
      email: email,
      pin: passwordlessOtp,
      password: newPassword,
      password_confirmation: confirmPassword,
    }))
      .done((data) => {
        const userId = data.data && data.data.id

        if (userId) {
          this.trackAuthConversion(userId)
          this.completeAuth(userId, {
            validPhone: data.meta.validPhone === true,
          })
          return
        }

        this.setState({
          loading: false,
          baseError: 'There was a problem creating your password. Please try again.',
        })
      })
      .fail((xhr) => {
        if (xhr.status !== 422) {
          document.location.href = '/500'
          return
        }

        this.setState({
          loading: false,
          baseError: 'There was a problem creating your password. Please try again.',
        })
      })
  }

  handleSignIn = () => {
    const email = this.trimmedEmail()
    const { password } = this.state

    if (!password) {
      this.setState({ baseError: 'Please enter your password' })
      return
    }

    this.setState({ loading: true, baseError: '', infoMessage: '' })

    const sessionObj = API.V3.userSession({
      email: email,
      password: password,
      reauth: true,
    })

    $.ajax(sessionObj)
      .done((result) => {
        const phone = (result.data && result.data.attributes && result.data.attributes.phone) || ''
        const userId = result.data && result.data.id
        const token = result.meta && result.meta.token

        this.attachOmniAuthToUser()
          .always(() => {
            this.setState({ contactPhone: phone, omniAuthDetails: null, loading: false }, () => {
              this.trackAuthConversion(userId, token)
              this.completeAuth(userId, result.meta)
            })
          })
      })
      .fail((xhr) => {
        let message = "You've entered the wrong password. Try again."
        try {
          const json = xhr && xhr.responseJSON
          if (json && json.errors && json.errors[0] && json.errors[0].detail) {
            message = json.errors[0].detail
          }
        } catch (e) {}
        this.setState({ loading: false, baseError: message })
      })
      .always(this.clearLoading)
  }

  validateContactPhone = () => {
    const { contactPhone } = this.state

    return new Promise((resolve) => {
      $.ajax({
        url: this.contactValidationUrl(),
        dataType: 'json',
        data: { user_attributes: { contact_phone: contactPhone } },
      })
        .fail(() => {
          document.location.href = '/500'
          resolve(false)
        })
        .done((result) => {
          if (result.valid) {
            resolve(true)
          } else {
            this.setState({
              contactPhoneError: result.error_message || 'Please enter a valid contact number',
              baseError: '',
            })
            resolve(false)
          }
        })
    })
  }

  updateContactPhoneAndRetryOtp = () => {
    const { contactPhone } = this.state
    const userId = window.App && window.App.currentUserId

    if (!userId) {
      this.setState({ contactPhoneError: 'Please sign in again to continue.', baseError: '' })
      return
    }

    if (!contactPhone.trim()) {
      this.setState({ contactPhoneError: 'Please enter your contact number', baseError: '' })
      return
    }

    this.setState({ loading: true, baseError: '', contactPhoneError: '', infoMessage: '' })

    this.validateContactPhone().then((valid) => {
      if (!valid) {
        this.setState({ loading: false })
        return
      }

      $.ajax(API.V3.userUpdate(
        { 'contact-phone': contactPhone.trim() },
        userId,
        { forcePhoneVerificationReset: true }
      ))
        .done(() => {
          window.App.userPhoneIsValid = true
          window.App.userPhoneVerified = false
          this.proceedAfterAuth()
        })
        .fail((xhr) => {
          const fallbackMessage = 'Sorry, we could not update your contact number. Please try again.'
          const message = this.authErrorMessage(xhr, fallbackMessage)

          if (xhr && xhr.status === 401) {
            this.setState({
              loading: false,
              contactPhoneError: message,
              baseError: '',
            })
            return
          }

          try {
            const json = xhr && xhr.responseJSON
            const errorList = this.normalizeApiErrors(json && json.errors)
            if (errorList.length) {
              const fieldErrors = this.applyApiFieldErrors(errorList, message)
              if (fieldErrors.contactPhoneError || fieldErrors.nameError) {
                this.setState({ loading: false, ...fieldErrors })
                return
              }
              this.setState({
                loading: false,
                contactPhoneError: fieldErrors.baseError || message,
                baseError: '',
              })
              return
            }
          } catch (e) {}
          this.setState({ loading: false, contactPhoneError: message, baseError: '' })
        })
    })
  }

  handleSignUp = () => {
    const email = this.trimmedEmail()
    const { name, contactPhone, phoneUpdateAfterOtp } = this.state
    const userId = window.App && window.App.currentUserId

    if (userId && phoneUpdateAfterOtp) {
      this.updateContactPhoneAndRetryOtp()
      return
    }

    let nameError = ''
    let contactPhoneError = ''

    if (!name.trim()) {
      nameError = 'Please enter your first name'
    }

    if (!contactPhone.trim()) {
      contactPhoneError = 'Please enter your contact number'
    }

    if (nameError || contactPhoneError) {
      this.setState({ nameError, contactPhoneError, baseError: '' })
      return
    }

    this.setState({
      loading: true,
      baseError: '',
      nameError: '',
      contactPhoneError: '',
      infoMessage: '',
    })

    this.validateContactPhone().then((valid) => {
      if (!valid) {
        this.setState({ loading: false })
        return
      }

      const userObj = API.V3.userCreate(
        {
          email: email,
          name: name.trim(),
          phone: contactPhone.trim(),
          'autogenerate-password': true,
        },
        {
          type: 'email-preferences',
          attrs: {
            'ss-update': true,
            'sms-offers': true,
          },
        }
      )

      $.ajax(userObj)
        .done((data) => {
          const meta = data.meta || {}
          this.trackAuthConversion(data.data.id, meta.token)
          this.completeAuth(data.data.id, meta, { skipUserFetch: true })
        })
        .fail((xhr) => {
          const message = 'Sorry, we could not create your account. Please try again.'
          try {
            const json = xhr && xhr.responseJSON
            const errorList = this.normalizeApiErrors(json && json.errors)
            if (errorList.length) {
              this.setState({
                loading: false,
                ...this.applyApiFieldErrors(errorList, message),
              })
              return
            }
          } catch (e) {}
          this.setState({ loading: false, baseError: message })
        })
    })
  }

  needsPhoneVerification = () => {
    if (!this.shouldOfferPhoneOtpAfterAuth()) {
      return false
    }

    return coreUtils.userHasValidPhone() && !coreUtils.userHasVerifiedPhone()
  }

  needsPhoneCapture = () => {
    return !coreUtils.userHasValidPhone()
  }

  finishAuthFlow = () => {
    if (!coreUtils.userHasPhoneForCurrentFlow()) {
      const { authStage, phoneCaptureMode } = this.state
      this.setAuthStage(CHANGE_PHONE_STAGE, {
        loading: false,
        otpReturnStage: this.resolveOtpReturnStage(authStage),
        phoneCaptureMode: phoneCaptureMode || 'contact',
      })
      return
    }

    this.setState({ loading: false, oauthNewAccountPhoneCapture: false })
    this.props.onAuthSuccess()
  }

  proceedAfterAuth = () => {
    this.runWhenPhoneVerificationRequiredReady(() => {
      const { authStage, phoneCaptureMode, oauthNewAccountPhoneCapture } = this.state
      const otpReturnStage = this.resolveOtpReturnStage(authStage)

      if (this.needsPhoneCapture()) {
        this.setAuthStage(CHANGE_PHONE_STAGE, {
          loading: false,
          otpReturnStage: otpReturnStage,
          phoneCaptureMode: oauthNewAccountPhoneCapture ? 'contact' : (phoneCaptureMode || 'contact'),
          oauthNewAccountPhoneCapture: false,
        })
        return
      }

      if (this.needsPhoneVerification()) {
        if (coreUtils.userHasVerifiedPhone()) {
          this.finishAuthFlow()
          return
        }

        this.prepareOtpAfterAuth(otpReturnStage)
        return
      }

      this.setState({ loading: false })
      this.props.onAuthSuccess()
    })
  }

  openOtpStage = (extraState = {}) => {
    this.setAuthStage(OTP_STAGE, {
      loading: false,
      ...extraState,
    })
  }

  prepareOtpAfterAuth = (otpReturnStage) => {
    this.setState({ loading: true })

    $.ajax(API.V3.phoneVerificationGenerate())
      .done((data) => {
        const status = data.verification_status

        if (status === 'verified') {
          window.App = window.App || {}
          window.App.userPhoneVerified = true
          this.finishAuthFlow()
          return
        }

        if (status === 'unavailable' || status === 'skipped') {
          window.App = window.App || {}
          window.App.userPhoneIsValid = false
          window.App.userPhoneVerified = false

          if (coreUtils.phoneVerificationRequired()) {
            this.openOtpStage({
              otpReturnStage,
              openChangePhone: true,
              changePhoneError: 'Please enter a valid mobile number',
              otpInitialSetupComplete: false,
              otpInitialVerificationStatus: '',
            })
            return
          }

          this.openOtpStage({
            otpReturnStage,
            otpInitialSetupComplete: true,
            otpInitialVerificationStatus: status,
          })
          return
        }

        this.openOtpStage({
          otpReturnStage,
          otpInitialSetupComplete: true,
          otpInitialVerificationStatus: '',
        })
      })
      .fail(() => {
        // Prefer change-phone over remounting into a setup retry that can stick blank.
        if (coreUtils.phoneVerificationRequired()) {
          this.openOtpStage({
            otpReturnStage,
            openChangePhone: true,
            changePhoneError:
              'Sorry, we could not send a verification code. Please check your mobile number and try again.',
            otpInitialSetupComplete: false,
            otpInitialVerificationStatus: '',
          })
          return
        }

        this.openOtpStage({
          otpReturnStage,
          otpInitialSetupComplete: false,
          otpInitialVerificationStatus: '',
        })
      })
  }

  handleOtpUnavailable = () => {
    window.App = window.App || {}
    window.App.userPhoneIsValid = false
    window.App.userPhoneVerified = false

    if (coreUtils.phoneVerificationRequired()) {
      this.setState({
        openChangePhone: true,
        changePhoneError: 'Please enter a valid mobile number',
        otpSubmitError: '',
        infoMessage: '',
      })
    }
  }

  completeAuth = (userId, meta, options = {}) => {
    if (!userId) {
      this.setState({ loading: false, baseError: 'Sign-in failed. Please try again.' })
      return
    }

    window.App = window.App || {}
    window.App.currentUserId = userId
    this.syncUserSessionId(userId)
    if (meta) {
      if (meta.validPhone !== undefined) {
        window.App.userPhoneIsValid = meta.validPhone === true
      }
      if (meta.verifiedPhone !== undefined) {
        window.App.userPhoneVerified = meta.verifiedPhone === true
      }
    }

    if (options.skipUserFetch) {
      if (window.App.userPhoneIsValid === undefined) {
        window.App.userPhoneIsValid = true
      }
      if (window.App.userPhoneVerified === undefined) {
        window.App.userPhoneVerified = false
      }

      this.setState({ loading: false, phoneUpdateAfterOtp: false }, () => {
        this.proceedAfterAuth()
      })
      return
    }

    $.ajax(API.V3.userGet({ id: userId }))
      .done((response) => {
        if (response.meta) {
          window.App.userPhoneIsValid = response.meta.validPhone === true
          window.App.userPhoneVerified = response.meta.verifiedPhone === true
        }

        const phone = (response.data && response.data.attributes && response.data.attributes.phone) || ''
        this.setState(
          { contactPhone: phone || this.state.contactPhone },
          () => {
            this.proceedAfterAuth()
          }
        )
      })
      .fail((xhr) => {
        if (xhr && xhr.status === 401) {
          const message = this.authErrorMessage(xhr, '')
          this.setState({
            loading: false,
            baseError: message,
            contactPhoneError: this.state.authStage === SIGN_UP_STAGE ? message : '',
          })
          return
        }

        document.location.href = '/500'
      })
  }

  handleOtpVerified = () => {
    this.setState({ openChangePhone: false, changePhoneError: '' })
    this.finishAuthFlow()
  }

  handlePhoneUpdated = (phone) => {
    this.setState({
      contactPhone: phone,
      openChangePhone: false,
      changePhoneError: '',
      contactPhoneError: '',
      otpSubmitError: '',
    })
  }

  handleChangePhoneDismissed = () => {
    this.setState({
      openChangePhone: false,
      changePhoneError: '',
    })
  }

  openFacebookOAuthStage = () => {
    this.setAuthStage(FACEBOOK_OAUTH_STAGE, { baseError: '', infoMessage: '' })
  }

  openGoogleOAuthStage = () => {
    this.setAuthStage(GOOGLE_OAUTH_STAGE, { baseError: '', infoMessage: '' })
  }

  isOAuthProviderStage = (authStage) => (
    authStage === FACEBOOK_OAUTH_STAGE || authStage === GOOGLE_OAUTH_STAGE
  )

  handleFacebookLogin = () => {
    if (!window.FB) return

    this.setState({ loading: true, baseError: '', infoMessage: '' })

    window.FB.login((response) => {
      if (response.authResponse) {
        $.ajax({
          crossDomain: true,
          url: '/users/auth/facebook/callback?origin=job_modal',
          type: 'POST',
          data: response,
          headers: { 'Access-Control-Allow-Origin': '*' },
        })
          .done((result) => {
            this.handleOAuthResult(result)
          })
          .fail((xhr) => {
            const message = (xhr.responseJSON && xhr.responseJSON.error) || 'Facebook sign-in failed.'
            this.setState({ loading: false, baseError: message })
          })
      } else {
        this.setState({ loading: false })
      }
    }, { scope: 'email' })
  }

  handleGoogleLogin = () => {
    if (!window.gapi || !window.gapi.auth2) {
      this.setState({ baseError: 'Google sign-in is still loading. Please try again.' })
      return
    }

    this.setState({ loading: true, baseError: '', infoMessage: '' })

    window.gapi.auth2.authorize({
      response_type: 'code',
      client_id: '1044201461425-0kma4id2dmnmspp65el6223n7k3hr6l2.apps.googleusercontent.com',
      scope: 'email profile',
    }, (response) => {
      if (response && !response.error) {
        $.ajax({
          type: 'POST',
          url: '/users/auth/google_oauth2/callback?origin=job_modal',
          dataType: 'json',
          data: response,
        })
          .done((result) => {
            this.handleOAuthResult(result)
          })
          .fail((xhr) => {
            const message = (xhr.responseJSON && xhr.responseJSON.error) || 'Google sign-in failed.'
            this.setState({ loading: false, baseError: message })
          })
      } else {
        this.setState({ loading: false })
      }
    })
  }

  handleBack = () => {
    const { authStage } = this.state

    if (authStage === EMAIL_STAGE) {
      this.props.onBack()
      return
    }

    if (this.isOAuthProviderStage(authStage)) {
      this.setAuthStage(EMAIL_STAGE, { baseError: '', infoMessage: '' })
      return
    }

    if (authStage === CHANGE_PHONE_STAGE) {
      const { otpReturnStage } = this.state
      this.setAuthStage(otpReturnStage || SIGN_UP_STAGE, {
        baseError: '',
        infoMessage: '',
        contactPhoneError: '',
        phoneUpdateAfterOtp: false,
      })
      return
    }

    if (authStage === PASSWORDLESS_OTP_STAGE) {
      this.clearPasswordlessResendFeedback()
      this.setAuthStage(PASSWORDLESS_OPTIONS_STAGE, {
        passwordlessOtp: '',
        baseError: '',
        infoMessage: '',
        lastOtpMethod: null,
      })
      return
    }

    if (authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE) {
      this.setAuthStage(PASSWORDLESS_OTP_STAGE, {
        newPassword: '',
        confirmPassword: '',
        baseError: '',
      })
      return
    }

    if (authStage === PASSWORDLESS_OPTIONS_STAGE) {
      if (this.state.passwordlessFromSignIn) {
        this.setAuthStage(SIGN_IN_STAGE, {
          baseError: '',
          infoMessage: '',
          otpReturnStage: SIGN_IN_STAGE,
        })
      } else {
        this.setAuthStage(EMAIL_STAGE, { baseError: '', infoMessage: '' })
      }
      return
    }

    this.setAuthStage(EMAIL_STAGE, {
      baseError: '',
      infoMessage: '',
      password: '',
      name: '',
      contactPhone: '',
      nameError: '',
      contactPhoneError: '',
      phoneUpdateAfterOtp: false,
    })
  }

  handleContinue = () => {
    const { authStage } = this.state

    if (authStage === EMAIL_STAGE) {
      this.handleEmailContinue()
    } else if (authStage === FACEBOOK_OAUTH_STAGE) {
      this.handleFacebookLogin()
    } else if (authStage === GOOGLE_OAUTH_STAGE) {
      this.handleGoogleLogin()
    } else if (authStage === SIGN_IN_STAGE) {
      this.handleSignIn()
    } else if (authStage === SIGN_UP_STAGE) {
      this.handleSignUp()
    } else if (authStage === CHANGE_PHONE_STAGE) {
      this.updateContactPhoneAndRetryOtp()
    } else if (authStage === PASSWORDLESS_OTP_STAGE) {
      this.handlePasswordlessOtpSubmit()
    } else if (authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE) {
      this.handlePasswordlessCreatePassword()
    }
  }

  isContinueDisabled = () => {
    const { authStage } = this.state
    const email = this.trimmedEmail()

    if (authStage === EMAIL_STAGE) {
      return !email || !isValidEmailFormat(email)
    }

    if (authStage === SIGN_IN_STAGE) {
      return !this.state.password
    }

    if (authStage === SIGN_UP_STAGE) {
      const userId = window.App && window.App.currentUserId
      if (userId) {
        return !this.state.contactPhone.trim()
      }
      return !this.state.name.trim() || !this.state.contactPhone.trim()
    }

    if (authStage === CHANGE_PHONE_STAGE) {
      return !this.state.contactPhone.trim()
    }

    if (authStage === PASSWORDLESS_OTP_STAGE) {
      return true
    }

    if (authStage === PASSWORDLESS_OPTIONS_STAGE) {
      return true
    }

    if (authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE) {
      return !this.state.newPassword || !this.state.confirmPassword
    }

    if (this.isOAuthProviderStage(authStage)) {
      return false
    }

    return true
  }

  isPasswordlessStage = (authStage) => {
    return authStage === PASSWORDLESS_OPTIONS_STAGE ||
      authStage === PASSWORDLESS_OTP_STAGE ||
      authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE
  }

  passwordlessPanelView = (authStage) => {
    if (authStage === PASSWORDLESS_OPTIONS_STAGE) return 'options'
    if (authStage === PASSWORDLESS_OTP_STAGE) return 'otp'
    if (authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE) return 'create_password'
    return null
  }

  openPasswordlessFromSignIn = () => {
    this.clearPasswordlessResendFeedback()
    this.setAuthStage(PASSWORDLESS_OPTIONS_STAGE, {
      baseError: '',
      infoMessage: '',
      passwordlessFromSignIn: true,
      lastOtpMethod: null,
    })
  }

  handleClosePasswordlessPanel = () => {
    this.handleBack()
  }

  showTerms = (authStage) => {
    return authStage === SIGN_IN_STAGE ||
      authStage === SIGN_UP_STAGE ||
      authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE
  }

  renderAccountFoundIntro = () => (
    <p className="text-base font-extrabold text-base-gray-text-main">
      We found your account. Log in to continue with this request.
    </p>
  )

  renderPasswordlessIntro = () => {
    const { authStage, passwordlessFromSignIn } = this.state

    if (authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE) {
      return null
    }

    if (!passwordlessFromSignIn) {
      return (
        <p className="text-base font-extrabold text-base-gray-text-main">
          We found your account. Create a password to continue.
        </p>
      )
    }

    if (authStage === PASSWORDLESS_OPTIONS_STAGE) {
      return this.renderAccountFoundIntro()
    }

    return null
  }

  renderEmailQuestion = () => (
    <p className="text-base font-extrabold text-base-gray-text-main">
      Posted a job before? We recommend using the same email address.
    </p>
  )

  authStageWrapperClassName = (isVisible) => (
    isVisible ? 'pt-0 md:pt-6' : 'hidden'
  )

  renderEmailInput = () => {
    const { email } = this.props
    const { authStage, emailError } = this.state
    const isEmailOnlyStage = authStage === EMAIL_STAGE
    const hasEmailError = Boolean(emailError) && isEmailOnlyStage

    return (
      <div className="flex flex-col gap-1">
        <label
          className={[
            'text-sm font-medium',
            hasEmailError ? 'text-base-red-900' : 'text-base-gray-text-main',
          ].join(' ')}
          htmlFor="dqr-email"
        >
          Email
        </label>
        <input
          id="dqr-email"
          type="email"
          className={[
            'input-text-base w-full h-[42px] text-sm font-normal',
            hasEmailError ? 'border-2 border-base-red-main focus:border-base-red-main' : '',
          ].join(' ')}
          value={email}
          autoComplete="email"
          readOnly={!isEmailOnlyStage}
          onChange={isEmailOnlyStage ? this.handleEmailChange : undefined}
        />
      </div>
    )
  }

  renderEmailFieldSection = () => {
    const { authStage, emailError } = this.state
    const showEmailErrorBanner = authStage === EMAIL_STAGE && Boolean(emailError)

    return (
      <div className="flex flex-col gap-4">
        {showEmailErrorBanner && <DangerAlert message={emailError} />}
        {this.renderEmailInput()}
      </div>
    )
  }

  renderEmailBlock = () => {
    const { authStage } = this.state
    const isOAuthProviderStage = this.isOAuthProviderStage(authStage)

    return (
      <div className="flex flex-col gap-5 md:gap-6">
        {this.renderEmailQuestion()}
        {!isOAuthProviderStage && this.renderEmailFieldSection()}
      </div>
    )
  }

  renderSocialAuthProviderStage = () => {
    const { authStage, baseError } = this.state
    const provider = authStage === FACEBOOK_OAUTH_STAGE ? 'facebook' : 'google'

    return (
      <div className="flex flex-col gap-5 md:gap-6">
        {this.renderEmailQuestion()}
        {baseError && <DangerAlert message={baseError} />}
        <SocialAuthProviderPanel provider={provider} />
        {this.renderTerms()}
      </div>
    )
  }

  isOAuthExistingSignIn = () => {
    const { authStage, omniAuthDetails } = this.state
    return authStage === SIGN_IN_STAGE && Boolean(omniAuthDetails && omniAuthDetails.oauth_email_exists)
  }

  renderStandardSignInBlock = () => {
    const passwordError = this.state.baseError
    const hasPasswordError = Boolean(passwordError)

    return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        this.handleSignIn()
      }}
    >
      {this.renderAccountFoundIntro()}
      <div className="space-y-1">
        <label
          className={[
            'text-sm font-medium',
            hasPasswordError ? 'text-base-red-900' : 'text-base-gray-text-main',
          ].join(' ')}
          htmlFor="dqr-password"
        >
          Password
        </label>
        <input
          id="dqr-password"
          type="password"
          className={[
            'input-text-base w-full h-[42px] text-sm font-normal',
            hasPasswordError ? 'border-2 border-base-red-main focus:border-base-red-main' : '',
          ].join(' ')}
          value={this.state.password}
          autoComplete="current-password"
          onChange={(e) => this.setState({ password: e.target.value, baseError: '', infoMessage: '' })}
        />
        {hasPasswordError && (
          <p className="text-sm font-normal text-base-red-900">{passwordError}</p>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="text-base font-extrabold text-base-link underline"
          onClick={this.openPasswordlessFromSignIn}
        >
          Forgot Password?
        </button>
      </div>
    </form>
    )
  }

  renderOAuthExistingSignInBlock = () => {
    const { omniAuthDetails, password, baseError } = this.state
    const hasPasswordError = Boolean(baseError)
    const provider = omniAuthDetails && omniAuthDetails.oauth_provider

    return (
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          this.handleSignIn()
        }}
      >
        <div className="rounded-5 bg-base-gray-50 p-3 text-center">
          <p className="text-base font-medium text-base-gray-text-main">
            We&apos;ve found your existing account! Please enter your password to login and proceed.
          </p>
        </div>
        {provider && (
          <div className="rounded-5 bg-base-gray-50 p-3">
            <p className="text-base font-medium text-base-gray-text-main">
              We will connect your {provider} account once you&apos;re logged in.
            </p>
          </div>
        )}
        <p className="text-base md:text-lg font-extrabold text-base-gray-text-main">
          Login
        </p>
        <div className="space-y-1">
          <input
            id="dqr-password"
            type="password"
            className={[
              'input-text-base w-full h-[42px] text-sm font-normal',
              hasPasswordError ? 'border-2 border-base-red-main focus:border-base-red-main' : '',
            ].join(' ')}
            placeholder="Password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => this.setState({ password: e.target.value, baseError: '', infoMessage: '' })}
          />
          {hasPasswordError && (
            <p className="text-sm font-normal text-base-red-900">
              {baseError} Please try again or
            </p>
          )}
        </div>
        <div className={hasPasswordError ? 'text-center' : 'flex justify-end'}>
          <button
            type="button"
            className="text-base font-extrabold text-base-link underline"
            onClick={this.openPasswordlessFromSignIn}
          >
            Reset your password
          </button>
        </div>
      </form>
    )
  }

  renderSignInBlock = () => {
    if (this.isOAuthExistingSignIn()) {
      return this.renderOAuthExistingSignInBlock()
    }

    return this.renderStandardSignInBlock()
  }

  renderContactPhoneCaptureBlock = () => {
    const { contactPhone, contactPhoneError, baseError } = this.state

    return (
      <div className="flex flex-col gap-4 md:gap-6">
        <p className="text-center text-lg md:text-xl font-extrabold text-base-gray-text-main">
          Please enter a phone number so business can contact you
        </p>
        {baseError && <DangerAlert message={baseError} />}
        <div className="w-full">
          {this.renderFormField({
            id: 'dqr-change-contact-phone',
            label: 'Contact number',
            type: 'tel',
            value: contactPhone,
            error: contactPhoneError,
            placeholder: '0491 570 110',
            hideLabelOnMobile: true,
            autoComplete: 'tel',
            inputClassName: 'text-center',
            onChange: (e) => this.setState({
              contactPhone: e.target.value,
              contactPhoneError: '',
              baseError: '',
              infoMessage: '',
            }),
          })}
        </div>
        <p className="text-center text-sm font-medium text-base-gray-text-secondary">
          e.g. 0491 570 110
        </p>
      </div>
    )
  }

  renderUpdatePhoneBlock = () => {
    const { contactPhone, contactPhoneError, baseError } = this.state
    const hasError = Boolean(contactPhoneError)

    return (
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="flex flex-col gap-1 text-center">
          <p className="text-sm font-extrabold tracking-wide text-base-blue-main uppercase">
            Update your mobile number
          </p>
          <h2 className="text-xl font-extrabold text-base-gray-text-main">
            What&apos;s your new number?
          </h2>
          <p className="text-base font-medium text-base-gray-text-main">
            Businesses will use this to get in touch about your job. e.g. 0491 123 456
          </p>
        </div>

        {(baseError || contactPhoneError) && (
          <div className="w-full md:max-w-[350px] md:mx-auto">
            <DangerAlert message={contactPhoneError || baseError} />
          </div>
        )}

        <div className="flex flex-col items-center gap-4 w-full md:max-w-[350px] md:mx-auto">
          <input
            id="dqr-change-contact-phone"
            type="tel"
            inputMode="tel"
            className={[
              'w-full h-12 px-3 rounded-5 text-center text-xl font-extrabold text-base-gray-text-main focus:outline-none',
              hasError
                ? 'border-2 border-base-red-main focus:border-base-red-main'
                : (contactPhone.length > 0 ? 'border border-base-border focus:border-2 focus:border-base-blue-main' : 'border-2 border-base-blue-main'),
            ].join(' ')}
            placeholder="0412 345 678"
            value={contactPhone}
            autoComplete="tel"
            onChange={(e) => this.setState({
              contactPhone: e.target.value,
              contactPhoneError: '',
              baseError: '',
              infoMessage: '',
            })}
          />
          {hasError && !baseError && (
            <p className="text-sm font-normal text-base-red-900 text-center">{contactPhoneError}</p>
          )}
        </div>
      </div>
    )
  }

  renderChangePhoneBlock = () => {
    if (this.state.phoneCaptureMode === 'contact') {
      return this.renderContactPhoneCaptureBlock()
    }

    return this.renderUpdatePhoneBlock()
  }

  renderPasswordlessPanel = () => {
    const { authStage, contactPhone } = this.state
    const view = this.passwordlessPanelView(authStage)

    if (!view) return null

    const noPasswordAccount = !this.state.passwordlessFromSignIn

    return (
      <div className="flex flex-col gap-4">
        {this.renderPasswordlessIntro()}
        <PasswordlessAuthPanel
          view={view}
          noPasswordAccount={noPasswordAccount && authStage === PASSWORDLESS_OPTIONS_STAGE}
          maskedPhone={maskPhoneForSms(contactPhone)}
          validMobileNumber={this.state.validMobileNumber}
          passwordlessOtp={this.state.passwordlessOtp}
          otpError={authStage === PASSWORDLESS_OTP_STAGE ? this.state.baseError : ''}
          createPasswordError={
            authStage === PASSWORDLESS_CREATE_PASSWORD_STAGE ? this.state.baseError : ''
          }
          newPassword={this.state.newPassword}
          confirmPassword={this.state.confirmPassword}
          showNewPassword={this.state.showNewPassword}
          showConfirmPassword={this.state.showConfirmPassword}
          infoMessage={this.state.infoMessage}
          loading={this.state.loading}
          loadingEmailOtp={this.state.loadingEmailOtp}
          loadingSmsOtp={this.state.loadingSmsOtp}
          loadingSubmit={this.state.loadingPasswordlessSubmit}
          lastOtpMethod={this.state.lastOtpMethod}
          passwordlessResendCooldownSecondsRemaining={this.state.passwordlessResendCooldownSecondsRemaining}
          canResendPasswordlessOtp={this.canResendPasswordlessOtp()}
          onSendEmail={() => this.sendPasswordlessOtp('email')}
          onSendSms={() => this.sendPasswordlessOtp('sms')}
          onClose={this.handleClosePasswordlessPanel}
          onOtpChange={(e) => this.setState({ passwordlessOtp: e.target.value, baseError: '' })}
          onSubmitOtp={this.handlePasswordlessOtpSubmit}
          onResendEmail={() => this.sendPasswordlessOtp('email', { isResend: true })}
          onResendSms={() => this.sendPasswordlessOtp('sms', { isResend: true })}
          onNewPasswordChange={(e) => this.setState({ newPassword: e.target.value, baseError: '' })}
          onConfirmPasswordChange={(e) => this.setState({ confirmPassword: e.target.value, baseError: '' })}
          onToggleNewPassword={() => this.setState({ showNewPassword: !this.state.showNewPassword })}
          onToggleConfirmPassword={() => this.setState(
            { showConfirmPassword: !this.state.showConfirmPassword }
          )}
        />
      </div>
    )
  }

  renderSignUpBlock = () => {
    const userId = window.App && window.App.currentUserId
    const updatingPhone = Boolean(userId && this.state.phoneUpdateAfterOtp)
    const { name, contactPhone, nameError, contactPhoneError, baseError } = this.state

    return (
      <div className="flex flex-col gap-4">
        <p className="text-base font-extrabold text-base-gray-text-main">
          {updatingPhone
            ? 'Update your contact number'
            : 'No account found for this email. Create your account to continue.'}
        </p>
        {baseError && <DangerAlert message={baseError} />}
        {!updatingPhone && this.renderFormField({
          id: 'dqr-first-name',
          label: 'First name',
          value: name,
          error: nameError,
          placeholder: 'First name',
          hideLabel: true,
          autoComplete: 'given-name',
          onChange: (e) => this.setState({
            name: e.target.value,
            nameError: '',
            baseError: '',
            infoMessage: '',
          }),
        })}
        {this.renderFormField({
          id: 'dqr-contact-phone',
          label: 'Contact number',
          type: 'tel',
          value: contactPhone,
          error: contactPhoneError,
          placeholder: 'Contact number',
          hideLabel: !updatingPhone,
          autoComplete: 'tel',
          onChange: (e) => this.setState({
            contactPhone: e.target.value,
            contactPhoneError: '',
            baseError: '',
            infoMessage: '',
          }),
        })}
      </div>
    )
  }

  renderTerms = () => (
    <p className="py-2 text-base font-medium text-base-gray-text-main">
      By pressing &ldquo;Continue&rdquo; you agree to receive lead alerts and marketing messages by email &amp; SMS and to our{' '}
      <a
        href={TERMS_URL}
        className="text-base-link font-extrabold underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms &amp; Conditions
      </a>
      .
    </p>
  )

  renderBackOnlyActions = () => {
    const { loading, loadingEmailOtp, loadingSmsOtp, loadingPasswordlessSubmit } = this.state
    const isBusy = loading || loadingEmailOtp || loadingSmsOtp || loadingPasswordlessSubmit

    return (
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-neutral btn-base-large btn-inline-block text-base-gray-text-secondary"
          onClick={this.handleBack}
          disabled={isBusy}
        >
          <hero-icon-outline name="arrow-left" class="h-4 w-4 text-base-gray-text-secondary" />
          <span className="text-base font-medium">Back</span>
        </button>
      </div>
    )
  }

  renderStepActions = () => {
    const { loading, authStage } = this.state
    const { saving } = this.props
    const isBusy = loading || saving
    const disabled = this.isContinueDisabled()
    const showContinue = authStage !== PASSWORDLESS_OPTIONS_STAGE &&
      authStage !== PASSWORDLESS_OTP_STAGE

    if (!showContinue) {
      return this.renderBackOnlyActions()
    }

    return (
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-neutral btn-base-large btn-inline-block text-base-gray-text-secondary"
          onClick={this.handleBack}
          disabled={isBusy}
        >
          <hero-icon-outline name="arrow-left" class="h-4 w-4 text-base-gray-text-secondary" />
          <span className="text-base font-medium">Back</span>
        </button>
        <button
          type="button"
          className='btn-blue-main btn-base-large btn-inline-block'
          disabled={disabled || isBusy}
          onClick={this.handleContinue}
        >
          <span className="text-base font-medium">
            {saving ? 'Submitting...' : 'Continue'}
          </span>
          <hero-icon-outline
            name="arrow-right"
            class={`h-4 w-4 text-surface flex-shrink-0 ${isBusy ? 'invisible' : ''}`}
          />
        </button>
      </div>
    )
  }

  renderAccountMainContent = ({
    isEmailStage,
    isChangePhoneStage,
    isOAuthProviderStage,
    showGlobalInfoMessage,
    infoMessage,
    onClose,
    showActionsInForm,
    hideEmailSection,
  }) => (
    <div className='contents'>
      {isChangePhoneStage ? (
        onClose ? (
          <div className="hidden md:flex shrink-0 justify-end">
            <WizardCloseButton onClick={onClose} />
          </div>
        ) : null
      ) : (
        <StepHeader
          eyebrow="Account & Notifications"
          title="Sign in to Receive Quotes"
          action={onClose ? (
            <WizardCloseButton compact onClick={onClose} />
          ) : null}
        />
      )}
      <div className="flex flex-col gap-5 md:gap-6">
        {isOAuthProviderStage ? (
          this.renderSocialAuthProviderStage()
        ) : (
          <div className='contents'>
            {!hideEmailSection && this.renderEmailQuestion()}
            {!hideEmailSection && this.renderEmailFieldSection()}
            {!isEmailStage && (
              <div className='contents'>
                <div className={this.authStageWrapperClassName(this.state.authStage === SIGN_IN_STAGE)}>
                  {this.renderSignInBlock()}
                </div>
                <div className={this.authStageWrapperClassName(this.state.authStage === SIGN_UP_STAGE)}>
                  {this.renderSignUpBlock()}
                </div>
                <div className={this.state.authStage === CHANGE_PHONE_STAGE ? 'pt-0' : 'hidden'}>
                  {this.renderChangePhoneBlock()}
                </div>
                <div className={this.authStageWrapperClassName(this.isPasswordlessStage(this.state.authStage))}>
                  {this.renderPasswordlessPanel()}
                </div>
              </div>
            )}
          </div>
        )}
        {this.showTerms(this.state.authStage) && this.renderTerms()}
        {showGlobalInfoMessage && (
          <p className="text-sm text-base-green-900">{infoMessage}</p>
        )}
        {showActionsInForm && (
          <div className="hidden md:block">
            {this.renderStepActions()}
          </div>
        )}
      </div>
    </div>
  )

  renderAccountFooter = ({ showStepActions, showSocialAuth, scrollInParent, saving, showActionsInForm, isPage }) => {
    const showMobileActions = showStepActions && showActionsInForm
    const showDesktopFooterActions = showStepActions && !showActionsInForm

    if (!showMobileActions && !showDesktopFooterActions && !showSocialAuth) return null

    return (
      <div className={[
        'shrink-0 flex flex-col gap-6 bg-surface',
        scrollInParent && isPage ? 'mt-auto md:mt-0' : 'mt-auto',
        showMobileActions ? 'pt-2 md:hidden' : 'pt-2 md:pt-6',
        scrollInParent ? 'sticky bottom-0 z-10' : '',
      ].join(' ')}
      >
        {showDesktopFooterActions && this.renderStepActions()}
        {showMobileActions && this.renderStepActions()}
        {showSocialAuth && (
          <SocialAuthButtons
            onFacebookClick={this.openFacebookOAuthStage}
            onGoogleClick={this.openGoogleOAuthStage}
            disabled={this.state.loading || saving}
          />
        )}
      </div>
    )
  }

  render() {
    const { authStage, baseError, infoMessage, contactPhone } = this.state
    const { isPage, pageBackLink, saving, scrollInParent, onClose } = this.props
    const isEmailStage = authStage === EMAIL_STAGE
    const isPhoneOtpStage = authStage === OTP_STAGE
    const isOAuthProviderStage = this.isOAuthProviderStage(authStage)
    const showSocialAuth = isEmailStage
    const showStepActions = !isPhoneOtpStage
    const showGlobalInfoMessage = infoMessage &&
      this.isPostEmailStage(authStage) &&
      authStage !== PASSWORDLESS_OTP_STAGE &&
      !this.isOAuthExistingSignIn()
    const usePageCenteredLayout = isPage && !isEmailStage
    const hideEmailSection = this.isOAuthExistingSignIn() ||
      (authStage === CHANGE_PHONE_STAGE && this.state.phoneCaptureMode === 'contact')
    const isChangePhoneStage = authStage === CHANGE_PHONE_STAGE
    const showActionsInForm = showStepActions && !isEmailStage

    const mainContent = this.renderAccountMainContent({
      isEmailStage,
      isChangePhoneStage,
      isOAuthProviderStage,
      showGlobalInfoMessage,
      infoMessage,
      onClose,
      showActionsInForm,
      hideEmailSection,
    })

    const footer = this.renderAccountFooter({
      showStepActions,
      showSocialAuth,
      scrollInParent,
      saving,
      showActionsInForm,
      isPage,
    })

    const backLinkSlot = isPage && pageBackLink ? (
      <div className="shrink-0">{pageBackLink}</div>
    ) : null

    const otpPanel = this.state.otpPanelMounted && (
      <PhoneOtpVerification
        active={isPhoneOtpStage}
        phoneNumber={contactPhone}
        saving={saving}
        initialSetupComplete={this.state.otpInitialSetupComplete}
        initialVerificationStatus={this.state.otpInitialVerificationStatus}
        submitError={this.state.otpSubmitError}
        openChangePhone={this.state.openChangePhone}
        changePhoneError={this.state.changePhoneError}
        onClose={onClose}
        onClearSubmitError={() => this.setState({ otpSubmitError: '' })}
        onVerified={this.handleOtpVerified}
        onUnavailable={this.handleOtpUnavailable}
        onPhoneUpdated={this.handlePhoneUpdated}
        onChangePhoneDismissed={this.handleChangePhoneDismissed}
      />
    )

    if (isPhoneOtpStage) {
      if (usePageCenteredLayout) {
        return (
          <PageOtpShell backLink={pageBackLink}>
            {otpPanel}
          </PageOtpShell>
        )
      }

      return (
        <div className={[
          'flex flex-col px-4 pt-6 pb-4 max-md:flex-1 max-md:min-h-0 max-md:h-full md:flex-none md:h-auto',
          isPage ? 'md:px-0 md:py-0' : 'lg:px-12 lg:pt-8 lg:pb-6',
        ].join(' ')}
        >
          {!usePageCenteredLayout && backLinkSlot}
          {otpPanel}
        </div>
      )
    }

    if (usePageCenteredLayout) {
      return (
        <div className="dqr-page-account-shell flex flex-col min-h-0 w-full">
          {backLinkSlot}
          <div className="dqr-page-account-body flex flex-col min-h-0 gap-6">
            <div
              className={[
                'min-h-0 overflow-y-auto overflow-x-hidden',
                scrollInParent ? 'md:overflow-visible md:h-auto' : 'flex-1',
              ].join(' ')}
            >
              {mainContent}
            </div>
            {footer}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-1 flex-col min-h-0 h-full gap-6 px-4 py-8 md:gap-8 lg:p-12">
        <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto overflow-x-hidden md:gap-8">
          {mainContent}
        </div>
        {footer}
      </div>
    )
  }
}
